/* global globalThis */
import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { Sector ,
  Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";

import {
  AlertTriangle, CheckCircle, XCircle, Database, Loader,
  X, Filter, Search,
  ChevronDown, ChevronUp, ChevronsUpDown, ChevronLeft, ChevronRight,
} from "lucide-react";
import axiosClientPayload from "../../../../api-client/AxiosClientPayload";


// ─── Shared Color Palette ─────────────────────────────────────────────────────
const DIMENSION_COLORS = [
  "#3b82f6","#10b981","#f59e0b","#ef4444",
  "#8b5cf6","#ec4899","#06b6d4","#f97316",
];
const getDimColor = (index) => DIMENSION_COLORS[index % DIMENSION_COLORS.length];

const API_BASE_URL = process.env.REACT_APP_ISSUE_BASE_URL;

// ─── KPI Card Styles (matching dashboard.css) ─────────────────────────────────
const kpiCardStyle = {
  background: "white", borderRadius: "15px", padding: "25px",
  boxShadow: "0 10px 30px rgba(0,0,0,0.1)", border: "1px solid #e0e6ed",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  minHeight: "130px",
  height: "130px",        
  boxSizing: "border-box", 
};
const kpiTitleStyle = {
  fontSize: "14px", color: "#666", marginBottom: "10px", fontWeight: 600,
  textTransform: "uppercase", letterSpacing: "0.5px",
  display: "flex", alignItems: "center", justifyContent: "space-between",
  whiteSpace: "nowrap", overflow: "hidden",
  maxWidth: "100%",        
  gap: "6px",             
};
const kpiValueStyle = { fontSize: "30px", fontWeight: 700, lineHeight: 1, margin: 0 };
const kpiSubStyle   = { fontSize: "12px", opacity: 0.8, marginTop: "4px" };

// ─── KPI Card Component ───────────────────────────────────────────────────────
const KpiCard = ({ title, value, sub, icon: Icon, iconColor, valueColor, activeColor }) => {
  const [hovered, setHovered] = useState(false);
  
  const hoverStyle = hovered
    ? { transform: "translateY(-5px)", boxShadow: "0 0 6px 1px rgba(30,58,138,0.3)", border: "1px solid #1e3a8a" }
    : {};
  
  const cardStyle = {
    ...kpiCardStyle,
    ...(activeColor
      ? { borderColor: activeColor, boxShadow: `0 0 6px 1px ${activeColor}50` }
      : hoverStyle),
  };
  return (
    <button style={{ ...cardStyle, background: "white", cursor: "pointer", fontFamily: "inherit", fontSize: "inherit", width: "100%", textAlign: "left", display: "block" }} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <div style={{ ...kpiTitleStyle, ...(activeColor ? { color: activeColor } : {}) }}>
        <span>{title}</span>
        <Icon style={{ width: 20, height: 20, color: activeColor ?? iconColor, flexShrink: 0 }} />
      </div>
      <p style={{ ...kpiValueStyle, color: activeColor ?? valueColor }}>{value}</p>
      {sub && <p style={{ ...kpiSubStyle, color: activeColor ?? "#666" }}>{sub}</p>}
    </button>
  );
};
KpiCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  sub: PropTypes.string,
  icon: PropTypes.elementType.isRequired,
  iconColor: PropTypes.string.isRequired,
  valueColor: PropTypes.string.isRequired,
  activeColor: PropTypes.string,
};

// ─── Custom Active Pie Shape ──────────────────────────────────────────────────
const renderActiveShape = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
  return (
    <g>
      <text x={cx} y={cy - 12} textAnchor="middle" fill={fill} style={{ fontWeight: 700, fontSize: 13 }}>{payload.name}</text>
      <text x={cx} y={cy + 10} textAnchor="middle" fill="#374151" style={{ fontSize: 12 }}>{`${(percent * 100).toFixed(1)}%`}</text>
      <text x={cx} y={cy + 28} textAnchor="middle" fill="#6b7280" style={{ fontSize: 11 }}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </text>
      <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 8} startAngle={startAngle} endAngle={endAngle} fill={fill} />
      <Sector cx={cx} cy={cy} innerRadius={outerRadius + 12} outerRadius={outerRadius + 16} startAngle={startAngle} endAngle={endAngle} fill={fill} />
    </g>
  );
};

// ─── Custom Bar Tooltip ───────────────────────────────────────────────────────
const CustomBarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-xl p-4 min-w-[180px]">
      <p className="text-sm font-bold text-gray-800 mb-2 border-b border-gray-100 pb-2">{label}</p>
      {payload.map((entry) => (
        <div key={entry.name} className="flex items-center justify-between gap-4 mt-1">
          <span className="flex items-center gap-1.5 text-xs text-gray-600">
            <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: entry.fill }} />
            {entry.name}
          </span>
          <span className="text-xs font-semibold text-gray-800">{entry.value?.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};
CustomBarTooltip.propTypes = { active: PropTypes.bool, payload: PropTypes.arrayOf(PropTypes.object), label: PropTypes.string };

// ─── Clickable Legend ─────────────────────────────────────────────────────────
const ClickableLegend = ({ labels, activeDimension, onLegendClick }) => (
  <div className="flex flex-wrap justify-center gap-2 mt-2">
    {labels.map((label, index) => {
      const isActive = activeDimension?.name === label;
      const color = getDimColor(index);
      const inactiveClass = activeDimension ? "opacity-35 border-gray-200 hover:opacity-60" : "border-transparent hover:border-gray-200 hover:bg-gray-50";
      const btnClass = isActive ? "shadow-md scale-105 border-current" : inactiveClass;
      return (
        <button key={label} onClick={() => onLegendClick(label, index)}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all duration-200 ${btnClass}`}
          style={{ color }}>
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
          {label}
        </button>
      );
    })}
  </div>
);
ClickableLegend.propTypes = {
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeDimension: PropTypes.shape({ name: PropTypes.string.isRequired, color: PropTypes.string.isRequired }),
  onLegendClick: PropTypes.func.isRequired,
};

// ════════════════════════════════════════════════════════════════════════════
// DIMENSION FILTER DROPDOWN (top bar)
// ════════════════════════════════════════════════════════════════════════════
const getScoreColorHelper = (score) => {
  if (score >= 90) return "#22c55e";
  if (score >= 70) return "#f59e0b";
  return "#ef4444";
};

const DimensionFilterBar = ({
  dimensions,
  activeDimension,
  filteredStats,
  onSelect,
  onClear,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const getScoreColor = (score) => getScoreColorHelper(score);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* ── Dropdown trigger row ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
        <div ref={ref} style={{ position: "relative" }}>
          <button
            onClick={() => setOpen((o) => !o)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              height: 36, padding: "0 16px",
              background: activeDimension ? activeDimension.color : "#fff",
              border: `1.5px solid ${activeDimension ? activeDimension.color : "#d1d9e0"}`,
              borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600,
              color: activeDimension ? "#fff" : "#374151",
              boxShadow: activeDimension ? `0 2px 8px ${activeDimension.color}40` : "0 1px 4px rgba(0,0,0,0.06)",
              transition: "all 0.2s",
            }}
          >
            <Filter style={{ width: 14, height: 14 }} />
            {activeDimension ? `Dimension: ${activeDimension.name}` : "Filter by Dimension"}
            {activeDimension && (
              <button
                onClick={(e) => { e.stopPropagation(); onClear(); setOpen(false); }}
                style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 16, height: 16, borderRadius: "50%", background: "rgba(255,255,255,0.25)", cursor: "pointer", marginLeft: 2, border: "none", padding: 0 }}
                aria-label="Clear dimension filter"
                title="Clear"
              >
                <X style={{ width: 10, height: 10 }} />
              </button>
            )}
            <ChevronDown style={{ width: 13, height: 13, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.18s", marginLeft: 2, opacity: 0.8 }} />
          </button>

          {/* ── Dropdown menu ── */}
          {open && (
            <div style={{
              position: "absolute", top: "calc(100% + 6px)", right: 0, minWidth: 220,
              background: "#fff", border: "1.5px solid #e0e6ed", borderRadius: 10,
              boxShadow: "0 10px 30px rgba(0,0,0,0.12)", zIndex: 400, overflow: "hidden",
            }}>
              {/* ── CHANGE 2: header now has "Select Dimension" label + Select All / Clear All buttons ── */}
              <div style={{
                padding: "8px 14px", background: "#f8fafc",
                borderBottom: "1px solid #e0e6ed",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <span style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.08em", color: "#7a869a", fontWeight: 700 }}>
                  Select Dimension
                </span>
                <div style={{ display: "flex", gap: 8 }}>
                  {/* Select All — clears the active filter (shows all) */}
                  <button
                    onClick={() => { onClear(); setOpen(false); }}
                    style={{
                      fontSize: 9, color: "#4a6fa5", background: "none", border: "none",
                      cursor: "pointer", textDecoration: "underline", fontWeight: 600,
                      padding: 0,
                    }}
                  >
                    Select All
                  </button>
                  <span style={{ color: "#d1d9e0", fontSize: 9 }}>|</span>
                  {/* Clear All — also clears (same effect as Select All in single-select model) */}
                  <button
                    onClick={() => { onClear(); setOpen(false); }}
                    style={{
                      fontSize: 9, color: "#ef4444", background: "none", border: "none",
                      cursor: "pointer", textDecoration: "underline", fontWeight: 600,
                      padding: 0,
                    }}
                  >
                    Clear All
                  </button>
                </div>
              </div>

              {/* ── "All Dimensions" option REMOVED ── */}

              {/* dimension options */}
              {dimensions.map(({ name, color, index }) => {
                const isActive = activeDimension?.name === name;
                return (
                  <button key={name}
                    onClick={() => { onSelect(name, index); setOpen(false); }}
                    style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
                      cursor: "pointer", borderBottom: "1px solid #f0f4fa",
                      background: isActive ? "#f0f4fa" : "transparent",
                      transition: "background 0.12s",
                      border: "none",
                      width: "100%",
                      textAlign: "left",
                    }}
                    onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = "#f8fafc"; }}
                    onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                  >
                    <div style={{
                      width: 14, height: 14, borderRadius: 3,
                      background: isActive ? color : "#fff",
                      border: `1.5px solid ${isActive ? color : "#d1d9e0"}`,
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>
                      {isActive && <span style={{ fontSize: 9, color: "#fff", lineHeight: 1 }}>✓</span>}
                    </div>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, fontWeight: isActive ? 700 : 400, color: isActive ? color : "#374151" }}>{name}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Active filter stats banner ── */}
      {activeDimension && filteredStats && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexWrap: "wrap", gap: 12,
          padding: "12px 20px",
          background: `linear-gradient(135deg, ${activeDimension.color}10, ${activeDimension.color}06)`,
          border: `1px solid ${activeDimension.color}30`,
          borderLeft: `4px solid ${activeDimension.color}`,
          borderRadius: 10,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
            {/* ── CHANGE 1: "Filtered: X ×" pill REMOVED — only stats shown ── */}
            {filteredStats.total !== undefined && (
              <span style={{ fontSize: 13, color: "#64748b" }}>
                Records: <strong style={{ color: "#1e293b" }}>{filteredStats.total?.toLocaleString()}</strong>
              </span>
            )}
            {filteredStats.success !== undefined && (
              <span style={{ fontSize: 13, color: "#16a34a" }}>
                Passed: <strong>{filteredStats.success?.toLocaleString()}</strong>
              </span>
            )}
            {filteredStats.failed !== undefined && (
              <span style={{ fontSize: 13, color: "#dc2626" }}>
                Failed: <strong>{filteredStats.failed?.toLocaleString()}</strong>
              </span>
            )}
            {filteredStats.score !== undefined && (
              <span style={{ fontSize: 13, fontWeight: 700, color: getScoreColor(filteredStats.score) }}>
                Score: {filteredStats.score?.toFixed(1)}%
              </span>
            )}
          </div>

          <button
            onClick={onClear}
            style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              padding: "5px 12px", borderRadius: 6,
              border: "1px solid #d1d9e0", background: "#fff",
              fontSize: 11, color: "#64748b", cursor: "pointer",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.color = "#374151"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#64748b"; }}
          >
            <X style={{ width: 11, height: 11 }} /> Show All
          </button>
        </div>
      )}
    </div>
  );
};

// Note: getScoreColor used inside DimensionFilterBar — defined outside for reuse
function getScoreColor(score) {
  if (score >= 90) {
    return "#22c55e";
  }
  if (score >= 70) {
    return "#f59e0b";
  }
  return "#ef4444";
}

DimensionFilterBar.propTypes = {
  dimensions:      PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, color: PropTypes.string, index: PropTypes.number })).isRequired,
  activeDimension: PropTypes.shape({ name: PropTypes.string.isRequired, color: PropTypes.string.isRequired }),
  filteredStats:   PropTypes.shape({ total: PropTypes.number, success: PropTypes.number, failed: PropTypes.number, score: PropTypes.number }),
  onSelect:        PropTypes.func.isRequired,
  onClear:         PropTypes.func.isRequired,
};

// ════════════════════════════════════════════════════════════════════════════
// RECENT ANALYSIS COMPONENT
// ════════════════════════════════════════════════════════════════════════════

const STATUS_CONFIG = {
  pass: { label: "Low",      dot: "#7da87d", badgeBg: "#eef6ee", badgeBorder: "#b6d9b6", badgeText: "#2e6b2e" },
  warn: { label: "High",     dot: "#e6a817", badgeBg: "#fdf4e3", badgeBorder: "#e8cd8a", badgeText: "#7a4f00" },
  fail: { label: "Critical", dot: "#c47c7c", badgeBg: "#faeaea", badgeBorder: "#e0b0b0", badgeText: "#8a2a2a" },
};

const deriveStatus = (successRate) => {
  if (successRate >= 90) return "pass";
  if (successRate >= 60) return "warn";
  return "fail";
};

const getProgressBarColor = (value) => {
  if (value >= 90) return "#7da87d";
  if (value >= 60) return "#e6a817";
  return "#c47c7c";
};

const ProgressBar = ({ value }) => {
  const color = getProgressBarColor(value);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ width: 80, height: 6, background: "#e4e9f0", borderRadius: 3, overflow: "hidden", flexShrink: 0 }}>
        <div style={{ width: `${Math.min(value, 100)}%`, height: "100%", background: color, borderRadius: 3, transition: "width 0.4s ease" }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color }}>
        {typeof value === "number" ? value.toFixed(1) : value}%
      </span>
    </div>
  );
};
ProgressBar.propTypes = {
  value: PropTypes.number.isRequired,
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.warn;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "4px 12px", borderRadius: 20,
      border: `1px solid ${cfg.badgeBorder}`,
      background: cfg.badgeBg, color: cfg.badgeText,
      fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
    }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: cfg.dot, flexShrink: 0, display: "inline-block" }} />
      {cfg.label}
    </span>
  );
};
StatusBadge.propTypes = {
  status: PropTypes.string.isRequired,
};

const SortIcon = ({ field, sortKey, sortDir }) => {
  if (sortKey !== field) return <ChevronsUpDown style={{ width: 11, height: 11, opacity: 0.4, display: "inline", marginLeft: 3 }} />;
  return sortDir === "asc"
    ? <ChevronUp   style={{ width: 11, height: 11, display: "inline", marginLeft: 3 }} />
    : <ChevronDown style={{ width: 11, height: 11, display: "inline", marginLeft: 3 }} />;
};
SortIcon.propTypes = {
  field: PropTypes.string.isRequired,
  sortKey: PropTypes.string.isRequired,
  sortDir: PropTypes.string.isRequired,
};

// ─── API ──────────────────────────────────────────────────────────────────────

const fetchThreadHistoryList = async (payload) => {
  const response = await axiosClientPayload.post(
    `${API_BASE_URL}/thread-history/list`,
    payload
  );
  return response.data;
};

// ─── Fetch single thread detail ───────────────────────────────────────────────
const fetchThreadDetail = async (payload) => {
  const response = await axiosClientPayload.post(
    `${API_BASE_URL}/thread-history`,
    payload
  );
  return response.data;
};

const mapTableToRow = (table) => {
  // ── Use overall_analysis for metrics ─────────────────────────────────────
  const overall     = table.overall_analysis || {};
  const successRate = overall.overall_success_percentage ?? table.success_percentage ?? 0;

  let dateStr = "—";
  const rawDate = table.latest_timestamp || table.latest_analyzed_at || table.timestamp;
  if (rawDate) {
    try {
      dateStr = new Date(rawDate).toLocaleString("en-GB", {
  day: "2-digit", month: "short", year: "numeric",
  hour: "2-digit", minute: "2-digit", hour12: false,
});
    } catch (_) { /* noop */ }
  }

  const tablesAnalyzed = Array.isArray(table.tables_analyzed)
    ? table.tables_analyzed.join(", ")
    : overall.table_name || table.table_name || "—";

  return {
    id: table.thread_id || (
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `id-${Date.now().toString(36)}-${performance.now().toString(36).replace('.', '')}`
),
    table_name:     table.title || tablesAnalyzed || "—",
    title:          table.title || "—",
    date:           dateStr,
    date_ts:        rawDate ? new Date(rawDate).getTime() : 0,
    success_rate:   successRate,
    total_records:  overall.total_records        ?? table.total_records  ?? 0,
    failed_records: overall.total_failed_records ?? table.failed_records ?? 0,
    rules_applied:  overall.total_rules_applied  ?? table.table_count    ?? 0,
    status:         deriveStatus(successRate),
    raw:            table,
  };
};
const PAGE_SIZE = 5;

const RecentAnalysis = ({ threadPayload, preloadedRows = [], onRowClick }) => {
  const [rows, setRows] = useState(() => preloadedRows.map(mapTableToRow));
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [search, setSearch]           = useState("");
  const [sortKey, setSortKey]         = useState("date_ts");
  const [sortDir, setSortDir]         = useState("desc");
  const [page, setPage]               = useState(1);
  const [statusFilter, setStatusFilter] = useState(new Set(["pass","warn","fail"]));
  const [statusOpen, setStatusOpen]   = useState(false);
  const statusRef                     = useRef(null);

useEffect(() => {
  // ── If preloadedRows already have data, use them directly ──────────────
  if (preloadedRows.length > 0) {
    setRows(preloadedRows.map(mapTableToRow));
    return;
  }
  // ── Otherwise fall back to fetching via threadPayload ──────────────────
  if (!threadPayload) return;
  setLoading(true);
  setError(null);
  fetchThreadHistoryList(threadPayload)
    .then((data) => {
      const items =
        data?.threads ||
        data?.tables  ||
        data?.data    ||
        (Array.isArray(data) ? data : []);
      setRows(items.map(mapTableToRow));
    })
    .catch((err) => setError(err.message))
    .finally(() => setLoading(false));
}, [threadPayload, preloadedRows]);

  useEffect(() => {
    const handler = (e) => {
      if (statusRef.current && !statusRef.current.contains(e.target)) setStatusOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggleStatus = (s) => {
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(s)) {
        next.delete(s);
      } else {
        next.add(s);
      }
      return next;
    });
    setPage(1);
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
    setPage(1);
  };

  const filtered = rows
    .filter((r) => statusFilter.has(r.status))
    .filter((r) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        r.table_name.toLowerCase().includes(q) ||
        r.session_id?.toLowerCase().includes(q) ||
        r.date.toLowerCase().includes(q) ||
        r.title?.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1  : -1;
      return 0;
    });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged      = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const getPages = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [1];
    if (page > 3) pages.push("…");
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page < totalPages - 2) pages.push("…");
    pages.push(totalPages);
    return pages;
  };

  const statusAllSelected = statusFilter.size === 3;

  const thBase = {
    padding: "10px 14px", textAlign: "left", fontSize: 10,
    letterSpacing: "0.07em", textTransform: "uppercase",
    color: "#7a869a", background: "#f8fafc", fontWeight: 600,
    borderBottom: "1.5px solid #e0e6ed", whiteSpace: "nowrap",
    cursor: "pointer", userSelect: "none",
  };
  const tdBase = {
    padding: "11px 14px", fontSize: 11, verticalAlign: "middle",
    borderBottom: "1px solid #f0f4fa",
  };

  return (
    <div style={{
      background: "#fff", borderRadius: 12,
      border: "1px solid #e0e6ed",
      boxShadow: "0 10px 30px rgba(0,0,0,0.07)",
      padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#1e293b" }}>Recent Analysis</div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div ref={statusRef} style={{ position: "relative" }}>
            <button
              onClick={() => setStatusOpen((o) => !o)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 7,
                height: 32, padding: "0 14px",
                background: statusAllSelected ? "#f8fafc" : "#fff",
                border: `1.5px solid ${statusAllSelected ? "#d1d9e0" : "#4a6fa5"}`,
                borderRadius: 6, cursor: "pointer", fontSize: 11,
                color: statusAllSelected ? "#7a869a" : "#4a6fa5",
                fontWeight: statusAllSelected ? 400 : 700,
                transition: "all 0.15s",
              }}
            >
              <Filter style={{ width: 13, height: 13 }} />
              Status
              {!statusAllSelected && (
                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 16, height: 16, background: "#4a6fa5", color: "#fff", borderRadius: 20, fontSize: 9, fontWeight: 700, padding: "0 4px" }}>
                  {statusFilter.size}
                </span>
              )}
              <ChevronDown style={{ width: 12, height: 12, transform: statusOpen ? "rotate(180deg)" : "none", transition: "transform 0.18s" }} />
            </button>
            {statusOpen && (
              <div style={{ position: "absolute", top: "calc(100% + 5px)", right: 0, minWidth: 190, background: "#fff", border: "1.5px solid #d1d9e0", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 300, overflow: "hidden" }}>
                <div style={{ padding: "7px 12px", background: "#f8fafc", borderBottom: "1px solid #e0e6ed", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.07em", color: "#7a869a" }}>Filter by Status</span>
                  <button onClick={() => { setStatusFilter(statusAllSelected ? new Set(["fail"]) : new Set(["pass","warn","fail"])); setPage(1); }}
                    style={{ fontSize: 9, color: "#4a6fa5", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>
                    {statusAllSelected ? "Clear All" : "Select All"}
                  </button>
                </div>
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                  <button key={key} onClick={() => toggleStatus(key)}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", cursor: "pointer", borderBottom: "1px solid #f0f4fa", transition: "background 0.12s", border: "none", background: "transparent", width: "100%", textAlign: "left" }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#f8fafc"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                    <div style={{ width: 14, height: 14, border: `1.5px solid ${statusFilter.has(key) ? "#4a6fa5" : "#d1d9e0"}`, borderRadius: 3, background: statusFilter.has(key) ? "#4a6fa5" : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {statusFilter.has(key) && <span style={{ fontSize: 9, color: "#fff", lineHeight: 1 }}>✓</span>}
                    </div>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: cfg.dot, flexShrink: 0 }} />
                    <span style={{ fontSize: 11 }}>{cfg.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div style={{ position: "relative" }}>
            <Search style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", width: 13, height: 13, color: "#9ca3af", pointerEvents: "none" }} />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search Title…"
              style={{ height: 32, paddingLeft: 30, paddingRight: 10, borderRadius: 6, border: "1px solid #d1d9e0", background: "#f8fafc", fontSize: 11, color: "#1e293b", outline: "none", width: 190, transition: "all 0.2s" }}
              onFocus={(e) => { e.target.style.borderColor = "#4a6fa5"; e.target.style.background = "#fff"; e.target.style.boxShadow = "0 0 0 3px rgba(74,111,165,0.1)"; }}
              onBlur={(e)  => { e.target.style.borderColor = "#d1d9e0"; e.target.style.background = "#f8fafc"; e.target.style.boxShadow = "none"; }}
            />
          </div>
        </div>
      </div>

      {loading && <div style={{ display: "flex", justifyContent: "center", padding: "32px 0" }}><Loader className="w-7 h-7 text-blue-600 animate-spin" /></div>}
      {error && !loading && <div style={{ background: "#faeaea", border: "1px solid #e0b0b0", borderRadius: 8, padding: "12px 16px", color: "#8a2a2a", fontSize: 12 }}>⚠ Failed to load thread history: {error}</div>}

      {!loading && !error && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {[
                  { label: "Title",         key: "table_name"    },
                  { label: "Analyzed On",   key: "date_ts"       },
                  { label: "Success Rate",  key: "success_rate"  },
                  { label: "Total Records", key: "total_records" },
                  { label: "Issue Records", key: "failed_records"},
                  { label: "Rules Applied", key: "rules_applied" },
                  { label: "Status",        key: "status"        },
                ].map(({ label, key }) => (
                  <th key={key} style={thBase} onClick={() => handleSort(key)}>
                    {label}<SortIcon field={key} sortKey={sortKey} sortDir={sortDir} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paged.length === 0 && (
                <tr><td colSpan={7} style={{ ...tdBase, textAlign: "center", color: "#9ca3af", padding: "36px 0", fontSize: 13 }}>No sessions match your filters.</td></tr>
              )}
              {paged.map((row) => {
                const statusCfg = STATUS_CONFIG[row.status] || STATUS_CONFIG.warn;
                return (
                  <tr key={row.id} onClick={() => onRowClick?.(row)}
                    style={{ cursor: "pointer", transition: "background 0.1s" }}
                    onMouseEnter={(e) => Array.from(e.currentTarget.cells).forEach(c => (c.style.background = "#f0f4fa"))}
                    onMouseLeave={(e) => Array.from(e.currentTarget.cells).forEach(c => (c.style.background = "transparent"))}>
                    <td style={tdBase}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ width: 7, height: 7, borderRadius: "50%", background: statusCfg.dot, flexShrink: 0 }} />
                          <span style={{ fontWeight: 700, fontSize: 11, color: "#1e293b" }}>{row.table_name}</span>
                        </div>
                        <span style={{ fontSize: 9, color: "#7a869a", paddingLeft: 13, fontFamily: "monospace" }}>{row.session_id}</span>
                      </div>
                    </td>
                    <td style={{ ...tdBase, fontWeight: 600, color: "#374151" }}>{row.date}</td>
                    <td style={tdBase}><ProgressBar value={row.success_rate} /></td>
                    <td style={{ ...tdBase, color: "#374151" }}>{row.total_records.toLocaleString()}</td>
                    <td style={{ ...tdBase, color: "#c47c7c", fontWeight: 600 }}>{row.failed_records.toLocaleString()}</td>
                    <td style={{ ...tdBase, color: "#374151" }}>{row.rules_applied}</td>
                    <td style={tdBase}><StatusBadge status={row.status} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <span style={{ fontSize: 10, color: "#7a869a" }}>
            Showing {Math.min((page - 1) * PAGE_SIZE + 1, filtered.length)}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} session{filtered.length === 1 ? "" : "s"}
          </span>
          <div style={{ display: "flex", gap: 4 }}>
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
              style={{ width: 28, height: 28, borderRadius: 4, background: "#f8fafc", border: "1px solid #d1d9e0", display: "flex", alignItems: "center", justifyContent: "center", cursor: page === 1 ? "not-allowed" : "pointer", opacity: page === 1 ? 0.4 : 1 }}>
              <ChevronLeft style={{ width: 13, height: 13 }} />
            </button>
            {getPages().map((p) =>
              p == "…"
                ? <span key="ellipsis" style={{ width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#7a869a" }}>…</span>
                : <button key={`page-${p}`} onClick={() => setPage(p)} style={{ width: 28, height: 28, borderRadius: 4, background: p === page ? "#4a6fa5" : "#f8fafc", border: `1px solid ${p === page ? "#4a6fa5" : "#d1d9e0"}`, color: p === page ? "#fff" : "#374151", fontSize: 11, cursor: "pointer", fontWeight: p === page ? 700 : 400 }}>{p}</button>
            )}
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              style={{ width: 28, height: 28, borderRadius: 4, background: "#f8fafc", border: "1px solid #d1d9e0", display: "flex", alignItems: "center", justifyContent: "center", cursor: page === totalPages ? "not-allowed" : "pointer", opacity: page === totalPages ? 0.4 : 1 }}>
              <ChevronRight style={{ width: 13, height: 13 }} />
            </button>
          </div>
        </div>
      )}

      {!loading && !error && paged.length > 0 && (
        <div style={{ textAlign: "center", fontSize: 9, color: "#9ca3af", letterSpacing: "0.05em" }}>
          [ Click a row to drill down to column-level analysis ]
        </div>
      )}
    </div>
  );
};

RecentAnalysis.propTypes = {
  threadPayload: PropTypes.shape({
    token:                PropTypes.string.isRequired,
    organizationConfigId: PropTypes.string.isRequired,
    userId:               PropTypes.string.isRequired,
  }),
  preloadedRows: PropTypes.array,
  onRowClick:    PropTypes.func,
};

// ─── Python tokenizer patterns ────────────────────────────────────────────────
const PYTHON_PATTERNS = [
  { pattern: /^(#.*)/, color: '#6a9955' },
  { pattern: /^("(?:[^"\\]|\\.)*")/, color: '#ce9178' },
  { pattern: /^('(?:[^'\\]|\\.)*')/, color: '#ce9178' },
  { pattern: /^(import|def|return|for|in|if|else|elif|class|from|as|with|lambda|True|False|None)\b/, color: '#c586c0' },
  { pattern: /^(df|uuid|axis|str|int|float|len|print|range|type)\b/, color: '#9cdcfe' },
  { pattern: /^(\d+\.?\d*)/, color: '#b5cea8' },
  { pattern: /^([A-Za-z_]\w*)/, color: '#d4d4d4' },
  { pattern: /^(\s+)/, color: '#d4d4d4' },
  { pattern: /^([\s\S])/, color: '#d4d4d4' },
];

function tryMatchPattern(rest, patterns) {
  for (const { pattern, color } of patterns) {
    const m = rest.match(pattern);
    if (m) return { text: m[1], color, length: m[1].length };
  }
  return null;
}

function tokenizePythonLine(line) {
  const tokens = [];
  let pos = 0;
  while (pos < line.length) {
    const rest = line.slice(pos);
    const match = tryMatchPattern(rest, PYTHON_PATTERNS);
    if (match) {
      tokens.push({ text: match.text, color: match.color });
      pos += match.length;
    } else {
      break;
    }
  }
  return tokens;
}

function renderSqlLine(line, lineKey) {
  const escapeHtml = (str) =>
    str.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');
    const highlighted = escapeHtml(line)
    .replaceAll(/(--[^\r\n]*)/g, '<span style="color:#6a9955">$1</span>')
    .replaceAll(/\b(SELECT|UPDATE|SET|WHERE|FROM|CREATE|SEQUENCE|INSERT|DELETE|ALTER|DROP)\b/g,
      '<span style="color:#569cd6">$1</span>')
    .replaceAll(/\b(CAST|AS|NOT|NULL|AND|OR|IS|START|WITH|NEXT|VALUE|FOR)\b/g,
      '<span style="color:#569cd6">$1</span>')
    .replaceAll(/(\[\w+\])/g, '<span style="color:#9cdcfe">$1</span>')
    .replaceAll(/('[^']*')/g, '<span style="color:#ce9178">$1</span>');
  return <div key={lineKey} dangerouslySetInnerHTML={{ __html: highlighted || '&nbsp;' }} />;
}

function renderPythonLine(line, lineKey) {
  const pythonTokens = [];
  let remaining = line;
  const hashIdx = remaining.indexOf('#');
const commentMatch = hashIdx === -1 ? null : [null, remaining.slice(0, hashIdx), remaining.slice(hashIdx)];
  if (commentMatch) {
    remaining = commentMatch[1];
    const comment = commentMatch[2];
    const prefixTokens = remaining ? tokenizePythonLine(remaining) : [];
    prefixTokens.forEach((t, ti) => pythonTokens.push(<span key={`p${ti}-${t.text}`} style={{ color: t.color }}>{t.text}</span>));
    pythonTokens.push(<span key="comment" style={{ color: '#6a9955' }}>{comment}</span>);
    return <div key={lineKey}>{pythonTokens.length ? pythonTokens : '\u00a0'}</div>;
  }
  const tokens = tokenizePythonLine(line);
  return (
    <div key={lineKey}>
      {tokens.length > 0
        ? tokens.map((t, ti) => <span key={`${ti}-${t.text}`} style={{ color: t.color }}>{t.text}</span>)
        : '\u00a0'}
    </div>
  );
}

function renderCodeBlock(id, code) {
  const isSql = id.startsWith('sql');
  return (
    <div key={id} style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${isSql ? '#c8dff5' : '#c2e8d0'}`, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <div style={{
        padding: '10px 14px',
        background: isSql ? '#eaf3fc' : '#eaf7ef',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: `1px solid ${isSql ? '#c8dff5' : '#c2e8d0'}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, color: isSql ? '#3a7abf' : '#2e7d50' }}>
            {isSql ? '' : '</>'}
          </span>
          <span style={{ fontSize: 11, fontWeight: 700, color: isSql ? '#3a7abf' : '#2e7d50', letterSpacing: '.02em' }}>
            {isSql ? 'SQL Fix' : 'Python Fix'}
          </span>
        </div>
        <button
          onClick={() => navigator.clipboard?.writeText(code)}
          style={{
            fontSize: 10, padding: '3px 8px',
            background: 'transparent',
            border: `1px solid ${isSql ? '#a0c4e8' : '#90cfac'}`,
            borderRadius: 5, cursor: 'pointer',
            color: isSql ? '#3a7abf' : '#2e7d50',
            fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4,
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = isSql ? '#d6ebf9' : '#d4f0e0'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          ⧉ Copy
        </button>
      </div>
      <div style={{ background: '#1e1e1e', display: 'flex', overflow: 'auto' }}>
        <div style={{
          background: '#1e1e1e', paddingTop: 12, paddingBottom: 12,
          paddingLeft: 10, paddingRight: 10,
          borderRight: '1px solid #333', userSelect: 'none', flexShrink: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
        }}>
          {code.split('\n').map((_, li) => (
            <div key={`${id}-linenum-${li}`} style={{ fontSize: 10, color: '#555e6e', lineHeight: '1.6em', fontFamily: 'Courier New', minWidth: 18, textAlign: 'right' }}>
              {li + 1}
            </div>
          ))}
        </div>
        <pre style={{
          margin: 0, padding: '12px 14px',
          fontFamily: "'Courier New', Courier, monospace",
          fontSize: 10, lineHeight: 1.6,
          whiteSpace: 'pre-wrap', overflowX: 'auto', flex: 1,
          color: '#d4d4d4',
        }}>
          {code.split('\n').map((line, li) => {
            const lineKey = `${id}-line-${li}-${line.substring(0, 20).replaceAll(/[^a-z0-9]/gi, '')}`;
            return isSql ? renderSqlLine(line, lineKey) : renderPythonLine(line, lineKey);
          })}
        </pre>
      </div>
    </div>
  );
}

const renderColumnRow = (c, i, expandedColRows, setExpandedColRows, modalData) => {
  const hasFailed = (c.failed_rules ?? 0) > 0;
  const isExpanded = expandedColRows.has(i);

  const toggleExpand = () => {
    setExpandedColRows((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };

  const failedRule = (c.rules_detail || []).find(r => r.status?.includes('Fail')) || {};
  const failedRuleNames = (c.rules_detail || []).filter(r => r.status?.includes('Fail')).map(r => r.rule_name);

  const allErrorAnalysis = modalData?.tables?.flatMap(t =>
    t?.analyze_response?.error_analysis || t?.error_analysis || []
  ) || [];
  const matchedError = allErrorAnalysis.find(
    e => e.column_name === c.column_name && e.table_name === c.table_name
  ) || {};

  const sqlFix    = matchedError.sql_fix    || c.sql_fix    || `-- Fix for ${c.column_name}\n-- Review and correct data`;
  const pythonFix = matchedError.python_fix || c.python_fix || `# Fix for ${c.column_name}\n# Review and correct data`;
  const aiInsight = matchedError.ai_insight || c.ai_insight || `Column '${c.column_name}' has ${c.failed_rules} failed rule(s). Review the data and apply the recommended fix.`;
  const whatWrong  = matchedError.what_went_wrong || c.what_wrong  || `Field '${c.column_name}' violates: ${failedRuleNames.join(', ') || 'data quality rule'}`;
  const whatShould = matchedError.what_should_be  || c.what_should || 'All records must pass the applied data quality rules.';
  const samples    = failedRule.failed_samples || matchedError.failed_samples || c.sample_values || [];

  return (
    <React.Fragment key={`col-${i}`}>
      <tr style={{ background: hasFailed ? '#fdf0f0' : i % 2 === 0 ? '#fff' : '#f8fafc' }}>
        <td style={{ padding: '7px 8px', borderBottom: isExpanded ? 'none' : '1px solid #e4e9f0' }}>
          <code style={{ fontSize: 9, fontFamily: 'Courier New', color: '#4a6fa5' }}>{c.table_name || '—'}</code>
        </td>
        <td style={{ padding: '7px 8px', borderBottom: isExpanded ? 'none' : '1px solid #e4e9f0' }}>
          <strong style={{ fontFamily: 'Courier New', fontSize: 10 }}>{c.column_name || '—'}</strong>
          {c.source_table && c.source_table !== '-' && (
            <div style={{ fontSize: 9, color: '#7a869a' }}>← {c.source_table}.{c.source_column}</div>
          )}
        </td>
        <td style={{ padding: '7px 8px', borderBottom: isExpanded ? 'none' : '1px solid #e4e9f0' }}>
          {(c.mandatory === 'Y' || c.mandatory === true || c.is_mandatory)
            ? <span style={{ color: '#c47c7c', fontWeight: 700 }}>Y</span>
            : <span style={{ color: '#7a869a' }}>N</span>}
        </td>
        <td style={{ padding: '7px 8px', borderBottom: isExpanded ? 'none' : '1px solid #e4e9f0' }}>
          {(c.total_records ?? 0).toLocaleString()}
        </td>
        <td style={{ padding: '7px 8px', borderBottom: isExpanded ? 'none' : '1px solid #e4e9f0' }}>
          <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 20, background: '#d4ecd4', color: '#3a7a3a', marginRight: 3 }}>
            ✓ {c.passed_rules ?? 0}
          </span>
          {hasFailed && (
            <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 20, background: '#f5d5d5', color: '#8a2a2a' }}>
              ✗ {c.failed_rules}
            </span>
          )}
        </td>
        <td style={{ padding: '7px 8px', borderBottom: isExpanded ? 'none' : '1px solid #e4e9f0' }}>
          {hasFailed ? (
            <button
              onClick={toggleExpand}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                fontSize: 10, padding: '3px 10px',
                border: isExpanded ? 'none' : '1px solid #4a6fa5',
                borderRadius: 4,
                background: isExpanded ? '#4a6fa5' : '#fff',
                color: isExpanded ? '#fff' : '#4a6fa5',
                cursor: 'pointer', fontWeight: 600, transition: 'all 0.15s',
              }}
            >
              {isExpanded ? 'Close ▲' : 'Details ▾'}
            </button>
          ) : (
            <span style={{ color: '#b0b8c4', fontSize: 11 }}>—</span>
          )}
        </td>
      </tr>

      {hasFailed && isExpanded && (
        <tr style={{ background: '#f7f9fc' }}>
          <td colSpan={6} style={{ padding: '0 0 0 0', borderBottom: '2px solid #4a6fa5' }}>
            <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#2c3a50' }}>
                Issue Details: {c.column_name}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div style={{ background: '#fff', border: '1px solid #d4dae3', borderRadius: 6, padding: '10px 12px' }}>
                  <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.06em', color: '#7a869a', marginBottom: 5 }}>What Went Wrong</div>
                  <div style={{ fontSize: 11, color: '#2c3a50', lineHeight: 1.5 }}>{whatWrong}</div>
                </div>
                <div style={{ background: '#fff', border: '1px solid #d4dae3', borderRadius: 6, padding: '10px 12px' }}>
                  <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.06em', color: '#7a869a', marginBottom: 5 }}>Expected Behavior</div>
                  <div style={{ fontSize: 11, color: '#2c3a50', lineHeight: 1.5 }}>{whatShould}</div>
                </div>
              </div>
              <div style={{ background: '#eef3fa', border: '1px solid #c0d0e8', borderRadius: 6, padding: '10px 12px', fontSize: 11, color: '#2c3a50', lineHeight: 1.6 }}>
                <span style={{ color: '#f59e0b', marginRight: 5 }}></span>
                <strong style={{ color: '#4a6fa5' }}>AI Insight</strong><br />
                {aiInsight}
              </div>
              {samples.length > 0 && (
                <div>
                  <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.06em', color: '#7a869a', marginBottom: 5 }}>
                    Sample Failed Values
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {samples.map((s, si) => {
                      const isInvalid = !s || String(s).includes('INVALID') || String(s).toLowerCase() === 'null';
                      const sampleKey = `sample-${String(s).substring(0, 10).replaceAll(/[^a-z0-9]/gi, '')}-${si}`;
                      return (
                        <span key={sampleKey} style={{
                          fontSize: 10, fontFamily: 'Courier New',
                          padding: '2px 8px', borderRadius: 3,
                          background: isInvalid ? '#f5d5d5' : '#e4e9f0',
                          border: `1px solid ${isInvalid ? '#e0a0a0' : '#b0b8c4'}`,
                          color: isInvalid ? '#8a2a2a' : '#2c3a50',
                        }}>
                          Row {si + 1}: {isInvalid ? 'NULL/Empty' : String(s)}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[
                  { id: `sql-${i}`,    code: sqlFix    },
                  { id: `python-${i}`, code: pythonFix },
                ].map(({ id, code }) => renderCodeBlock(id, code))}
              </div>
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// MAIN: IssuePredictorDashboard
// ════════════════════════════════════════════════════════════════════════════
const IssuePredictorDashboard = ({ issuePredictorData, issueLoading, threadPayload }) => {
  const [ipActiveDimension, setIpActiveDimension] = useState(null);
  const [ipActivePieIndex,  setIpActivePieIndex]  = useState(null);
  const [selectedRow,       setSelectedRow]        = useState(null);
  const [modalTab,          setModalTab]           = useState('overview');
  const [modalData,         setModalData]          = useState(null);
const [modalLoading,      setModalLoading]       = useState(false);
  const [expandedColRows, setExpandedColRows] = useState(new Set());
   const [organizationConfigId, setOrganizationConfigId] = useState(
    () => localStorage.getItem("organization_config_id") || ""
  );

  useEffect(() => {
    const syncOrgConfig = () => {
      const stored = localStorage.getItem("organization_config_id");
      if (stored) setOrganizationConfigId(stored);
    };
    globalThis.addEventListener("localStorageUpdated", syncOrgConfig);
    globalThis.addEventListener("storage", syncOrgConfig);
    return () => {
      globalThis.removeEventListener("localStorageUpdated", syncOrgConfig);
      globalThis.removeEventListener("storage", syncOrgConfig);
    };
  }, []);

  const ipDimensionPieData = issuePredictorData?.dimension_pie_chart || [];

const ipDimensionBarData = useMemo(() => {
  const dimMap = {};
  (issuePredictorData?.threads || []).forEach((t) => {
    (t.dimension_bar_chart || []).forEach((d) => {
      if (!dimMap[d.dimension]) {
        dimMap[d.dimension] = {
          dimension:            d.dimension,
          success_records:      0,
          failed_records:       0,
          columns_with_issues:  0,
        };
      }
      dimMap[d.dimension].success_records     += d.success_records     ?? 0;
      dimMap[d.dimension].failed_records      += d.failed_records      ?? 0;
      dimMap[d.dimension].columns_with_issues += d.columns_with_issues ?? 0;
    });
  });
  return Object.values(dimMap).map((d) => {
    const total = d.success_records + d.failed_records || 1;
    return {
      ...d,
      success_percentage: Number.parseFloat(((d.success_records / total) * 100).toFixed(2)),
      failed_percentage:  Number.parseFloat(((d.failed_records  / total) * 100).toFixed(2)),
    };
  });
}, [issuePredictorData]);

  const handleSelectDimension = useCallback((name, index) => {
    if (ipActiveDimension?.name === name) { setIpActiveDimension(null); setIpActivePieIndex(null); }
    else { setIpActiveDimension({ name, color: getDimColor(index) }); setIpActivePieIndex(index); }
  }, [ipActiveDimension]);

  const handleIpPieClick = useCallback((data, index) => {
    const dimName = data?.name || data?.dimension;
    if (!dimName) return;
    handleSelectDimension(dimName, index);
  }, [handleSelectDimension]);

  const clearIpFilter = () => { setIpActiveDimension(null); setIpActivePieIndex(null); };

  const ipFilteredStats = ipActiveDimension
    ? (() => {
        const dim = ipDimensionBarData.find((d) => d.dimension === ipActiveDimension.name);
        if (!dim) return null;
        const success = dim.success_records ?? dim.success_count ?? 0;
        const failed  = dim.failed_records  ?? dim.failed_count  ?? 0;
        return { total: success + failed, success, failed, score: dim.success_percentage };
      })()
    : null;
// ── Aggregate dimension_pie_chart across all threads ── (must be before early return)
const threads = issuePredictorData?.threads || [];

const aggregatedDimData = useMemo(() => {
  const dimMap = {};
  threads.forEach((t) => {
    (t.dimension_pie_chart || []).forEach((d) => {
      if (!dimMap[d.dimension]) dimMap[d.dimension] = { name: d.dimension, count: 0 };
      dimMap[d.dimension].count += d.count ?? 0;
    });
  });
  const total = Object.values(dimMap).reduce((s, d) => s + d.count, 0);
  return Object.values(dimMap).map((d) => ({
    name: d.name,
    value: total > 0 ? Number.parseFloat(((d.count / total) * 100).toFixed(1)) : 0,
    count: d.count,
  }));
}, [threads]);

if (issueLoading) {
  return <div className="flex justify-center py-16"><Loader className="h-8 w-8 text-blue-600 animate-spin" /></div>;
}

const aggregatedData = issuePredictorData?.aggregated_overall_analysis || {};

const computedKpi = (() => {
  if (threads.length === 0) return null;
  let totalRecords   = 0;
  let failedRecords  = 0;
  let successRecords = 0;
  let columnsWithIssues = 0;

  threads.forEach((t) => {
    const oa = t.overall_analysis || {};
    totalRecords      += oa.total_records          ?? 0;
    failedRecords     += oa.total_failed_records   ?? 0;
    successRecords    += oa.total_success_records  ?? 0;
    columnsWithIssues += oa.columns_with_issues    ?? 0;
  });

  const successRate = totalRecords > 0
    ? Number.parseFloat(((successRecords / totalRecords) * 100).toFixed(2))
    : 0;

  return { totalRecords, failedRecords, successRate, columnsWithIssues };
})();

const activeDimBarData = ipActiveDimension
  ? ipDimensionBarData.find((d) => d.dimension === ipActiveDimension.name)
  : null;

// ── Merge: prefer aggregated_overall_analysis if present, else use computed ──
const baseKpi = {
  totalRecords:     aggregatedData.total_records            ?? computedKpi?.totalRecords     ?? 0,
  failedRecords:    aggregatedData.total_failed_records     ?? computedKpi?.failedRecords    ?? 0,
  successRate:      aggregatedData.overall_success_percentage ?? computedKpi?.successRate    ?? 0,
  columnsWithIssues: aggregatedData.columns_with_issues     ?? computedKpi?.columnsWithIssues ?? 0,
};

const ipKpi = ipActiveDimension && ipFilteredStats
  ? {
      totalRecords:      ipFilteredStats.total,
      failedRecords:     ipFilteredStats.failed,
      successRate:       ipFilteredStats.score,
      columnsWithIssues: activeDimBarData?.columns_with_issues ?? 0,
    }
  : baseKpi;

  const pieDimData = ipDimensionPieData.map((d) => ({ name: d.dimension, value: d.percentage || d.count || d.value || 0 }));
// ── Charts and KPI cards only render when issuePredictorData has chart data ──
 return (
  <>
  <div className="space-y-6">

    {/* ── Charts & KPIs — only shown when issuePredictorData has chart data ── */}
    {issuePredictorData && (
      <>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <KpiCard
            title={ipActiveDimension ? `Records (${ipActiveDimension.name})` : "Total Records"}
            value={ipKpi.totalRecords?.toLocaleString() || "0"}
            sub={ipActiveDimension ? "Dimension filtered" : undefined}
            icon={Database} iconColor="#2563eb" valueColor="#1e3a8a"
            activeColor={ipActiveDimension?.color}
          />
          <KpiCard
            title="Failed Records"
            value={ipKpi.failedRecords?.toLocaleString() || "0"}
            icon={AlertTriangle} iconColor="#ea580c" valueColor="#c2410c"
            activeColor={ipActiveDimension?.color}
          />
          <KpiCard
            title={ipActiveDimension ? "Passed Records" : "Success Rate"}
            value={ipActiveDimension ? (ipFilteredStats?.success?.toLocaleString() ?? "—") : `${ipKpi.successRate?.toFixed(2) || "0"}%`}
            icon={CheckCircle} iconColor="#16a34a" valueColor="#15803d"
            activeColor={ipActiveDimension?.color}
          />
          <KpiCard
            title="Columns with Issues"
            value={ipKpi.columnsWithIssues ?? "0"}
            sub={ipActiveDimension ? "Dimension filtered" : undefined}
            icon={XCircle} iconColor="#7c3aed" valueColor="#4c1d95"
            activeColor={ipActiveDimension?.color}
          />
        </div>
        

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {pieDimData.length > 0 && (
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-1">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Dimension Distribution</h3>
                  <p className="text-xs text-gray-400 mt-0.5">Click a slice or legend item to filter the dashboard</p>
                </div>
                {ipActiveDimension && <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full font-medium whitespace-nowrap">Click again to reset</span>}
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={pieDimData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                    innerRadius={55} outerRadius={90} activeIndex={ipActivePieIndex}
                    activeShape={renderActiveShape} onClick={(data, index) => handleIpPieClick(data, index)}
                    style={{ cursor: "pointer" }}>
                    {pieDimData.map((entry, index) => (
                      <Cell key={entry.name} fill={getDimColor(index)}
                        opacity={!ipActiveDimension || ipActiveDimension.name === entry.name ? 1 : 0.2}
                        style={{ transition: "opacity 0.25s ease" }} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend content={<ClickableLegend labels={pieDimData.map((d) => d.name)} activeDimension={ipActiveDimension}
                      onLegendClick={(label, index) => handleIpPieClick({ name: label }, index)} />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
   {/* ── Standalone Donut Summary ── */}
        {pieDimData.length > 0 && (
          <div style={{
            background: '#fff', borderRadius: 12,
            border: '1px solid #e0e6ed',
            boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
            padding: '20px 24px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>Dimension Distribution</div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>Click a slice or legend item to filter</div>
              </div>
              {ipActiveDimension && (
                <button
                  onClick={clearIpFilter}
                  style={{ width: 28, height: 28, borderRadius: 6, background: '#f1f5f9', border: '1px solid #e0e6ed', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontSize: 11 }}
                  title="Reset filter"
                >↺</button>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap' }}>
  {/* ── Donut ── */}
  <div style={{ flexShrink: 0, paddingLeft: 40 }}>
                <ResponsiveContainer width={200} height={200}>
                  <PieChart>
                    <Pie
                      data={pieDimData} dataKey="value" nameKey="name"
                      cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                      activeIndex={ipActivePieIndex} activeShape={renderActiveShape}
                      onClick={(data, index) => handleIpPieClick(data, index)}
                      style={{ cursor: 'pointer' }}
                    >
                      {pieDimData.map((entry, index) => (
                        <Cell
                          key={entry.name}
                          fill={getDimColor(index)}
                          opacity={!ipActiveDimension || ipActiveDimension.name === entry.name ? 1 : 0.2}
                          style={{ transition: 'opacity 0.25s ease' }}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(val) => [`${val}%`, 'Share']} />
                  </PieChart>
                </ResponsiveContainer>
                </div>

              {/* ── Legend table ── */}
              <div style={{ flex: 1, minWidth: 180 }}>
                {pieDimData.map((d, index) => {
                  const color = getDimColor(index);
                  const isActive = ipActiveDimension?.name === d.name;
                  const barData = ipDimensionBarData.find(b => b.dimension === d.name);
                  const failPct = barData?.failed_percentage ?? 0;
                  const hasIssue = failPct > 0;
                  return (
                    <div
                      key={d.name}
                      onClick={() => handleIpPieClick({ name: d.name }, index)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '7px 10px', borderRadius: 7, cursor: 'pointer',
                        marginBottom: 3,
                        background: isActive ? `${color}14` : 'transparent',
                        border: isActive ? `1px solid ${color}40` : '1px solid transparent',
                        opacity: ipActiveDimension && !isActive ? 0.4 : 1,
                        transition: 'all 0.18s',
                      }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f8fafc'; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
                      <span style={{ flex: 1, fontSize: 12, color: '#374151', fontWeight: isActive ? 700 : 400 }}>{d.name}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: hasIssue ? '#ef4444' : '#22c55e', minWidth: 42, textAlign: 'right' }}>
                        {d.value == null ? '—' : `${d.value}%`}
                      </span>
                    </div>
                  );
                })}
               <div style={{ fontSize: 9, color: '#94a3b8', textAlign: 'center', marginTop: 10, letterSpacing: '0.04em' }}>
                  [ Click slice or legend to filter all widgets ]
                </div>
              </div>

              {/* ── Horizontal Bar Charts ── */}
              <div style={{ flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 20 }}>

                {/* Success Records */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
                    Success Records by Dimension
                  </div>
                  {(() => {
                    const maxVal = Math.max(...ipDimensionBarData.map(x => x.success_records ?? 0), 1);
                    return ipDimensionBarData.map((d, index) => {
                      const pct = ((d.success_records ?? 0) / maxVal) * 100;
                      const isActive = !ipActiveDimension || ipActiveDimension.name === d.dimension;
                      return (
                        <div key={`s-${d.dimension}`}
                          onClick={() => handleIpPieClick({ name: d.dimension }, index)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleIpPieClick({ name: d.dimension }, index); } }}
                          role="button"
                          tabIndex={0}
                          aria-label={`Filter by ${d.dimension} dimension - ${d.success_records ?? 0} success records`}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7, cursor: 'pointer', opacity: isActive ? 1 : 0.3, transition: 'opacity 0.2s' }}>
                          <div style={{ width: 88, fontSize: 10, color: '#374151', textAlign: 'right', flexShrink: 0, fontWeight: ipActiveDimension?.name === d.dimension ? 700 : 400 }}>
                            {d.dimension}
                          </div>
                          <div style={{ flex: 1, height: 18, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: '#10b981', borderRadius: 4, transition: 'width 0.4s ease' }} />
                          </div>
                          <div style={{ width: 76, fontSize: 10, color: '#10b981', fontWeight: 600, flexShrink: 0 }}>
                            {(d.success_records ?? 0).toLocaleString()}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>

                {/* Failed Records */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
                    Failed Records by Dimension
                  </div>
                  {(() => {
                    const maxVal = Math.max(...ipDimensionBarData.map(x => x.failed_records ?? 0), 1);
                    return ipDimensionBarData.map((d, index) => {
                      const pct = ((d.failed_records ?? 0) / maxVal) * 100;
                      const isActive = !ipActiveDimension || ipActiveDimension.name === d.dimension;
                      return (
                        <div key={`f-${d.dimension}`}
                          onClick={() => handleIpPieClick({ name: d.dimension }, index)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleIpPieClick({ name: d.dimension }, index); } }}
                          role="button"
                          tabIndex={0}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7, cursor: 'pointer', opacity: isActive ? 1 : 0.3, transition: 'opacity 0.2s' }}>
                          <div style={{ width: 88, fontSize: 10, color: '#374151', textAlign: 'right', flexShrink: 0, fontWeight: ipActiveDimension?.name === d.dimension ? 700 : 400 }}>
                            {d.dimension}
                          </div>
                          <div style={{ flex: 1, height: 18, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: '#ef4444', borderRadius: 4, transition: 'width 0.4s ease' }} />
                          </div>
                          <div style={{ width: 76, fontSize: 10, color: '#ef4444', fontWeight: 600, flexShrink: 0 }}>
                            {(d.failed_records ?? 0).toLocaleString()}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>

             </div>

            </div>
          </div>
        )}
      </>
    )}
    {/* ── Donut Chart + Horizontal Bars — TWO SEPARATE CARDS ── */}
    {aggregatedDimData.length > 0 && (() => {
      const aggBarMap = {};
      threads.forEach((t) => {
        (t.dimension_bar_chart || []).forEach((d) => {
          if (!aggBarMap[d.dimension]) aggBarMap[d.dimension] = { success: 0, failed: 0 };
          aggBarMap[d.dimension].success += d.success_records ?? 0;
          aggBarMap[d.dimension].failed  += d.failed_records  ?? 0;
        });
      });

      // ── Compute bar values: if a dimension is selected, use only that dimension ──
      const selectedDimKey = ipActiveDimension?.name || null;
      const barSource = selectedDimKey && aggBarMap[selectedDimKey]
        ? { [selectedDimKey]: aggBarMap[selectedDimKey] }
        : aggBarMap;

      const totalSuccess = Object.values(barSource).reduce((s, v) => s + v.success, 0);
      const totalFailed  = Object.values(barSource).reduce((s, v) => s + v.failed,  0);
      const totalAll     = totalSuccess + totalFailed || 1;
      const successPct   = Number.parseFloat(((totalSuccess / totalAll) * 100).toFixed(1));
      const failedPct    = Number.parseFloat(((totalFailed  / totalAll) * 100).toFixed(1));


      return (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 3fr', gap: 20 }}>

          {/* ── LEFT CARD: Dimension Distribution ── */}
          <div style={{
            background: '#fff', borderRadius: 12,
            border: '1px solid #e0e6ed',
            boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
            padding: '20px 24px',
          }}>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>Dimension Distribution</div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>Click a slice or legend to filter</div>
              {ipActiveDimension && (
                <button
                  onClick={clearIpFilter}
                  style={{ marginTop: 8, fontSize: 10, padding: '3px 12px', borderRadius: 5, cursor: 'pointer', background: '#f1f5f9', border: '1px solid #e0e6ed', color: '#64748b', fontWeight: 600 }}
                >↺ Show All</button>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
              <div style={{ flexShrink: 0, paddingLeft: 24 }}>
  <ResponsiveContainer width={200} height={200}>
    <PieChart>
      <Pie
        data={aggregatedDimData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%" cy="50%"
                      innerRadius={52} outerRadius={85}
                      activeIndex={ipActivePieIndex}
                      activeShape={renderActiveShape}
                      onClick={(data, index) => handleIpPieClick({ name: data.name }, index)}
                      style={{ cursor: 'pointer' }}
                    >
                      {aggregatedDimData.map((entry, index) => (
                        <Cell
                          key={entry.name}
                          fill={getDimColor(index)}
                          opacity={!ipActiveDimension || ipActiveDimension.name === entry.name ? 1 : 0.25}
                          style={{ transition: 'opacity 0.2s ease' }}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(val, name) => [`${val}%`, name]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div style={{ flex: 1 }}>
                {aggregatedDimData.map((d, index) => {
                  const color = getDimColor(index);
                  const isActive = ipActiveDimension?.name === d.name;
                  return (
                    <button
                      key={d.name}
                      onClick={() => handleIpPieClick({ name: d.name }, index)}
                      aria-label={`Filter by ${d.name} dimension - ${d.value}%`}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '5px 8px', marginBottom: 2, borderRadius: 6,
                        cursor: 'pointer',
                        background: isActive ? `${color}15` : 'transparent',
                        border: isActive ? `1px solid ${color}40` : '1px solid transparent',
                        opacity: ipActiveDimension && !isActive ? 0.4 : 1,
                        transition: 'all 0.18s',
                        fontSize: 'inherit',
                        fontFamily: 'inherit',
                      }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f8fafc'; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <span style={{ width: 9, height: 9, borderRadius: '50%', background: color, flexShrink: 0 }} />
                      <span style={{ flex: 1, fontSize: 11, color: '#374151', fontWeight: isActive ? 700 : 400 }}>{d.name}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color, minWidth: 36, textAlign: 'right' }}>
                        {d.value === null ? '—' : `${d.value}%`}
                      </span>
                    </button>
                  );
                })}
                {ipActiveDimension && (
                  <button
                    onClick={clearIpFilter}
                    style={{
                      marginTop: 8, width: '100%', fontSize: 10,
                      padding: '4px 0', borderRadius: 5, cursor: 'pointer',
                      background: '#f1f5f9', border: '1px solid #e0e6ed',
                      color: '#64748b', fontWeight: 600,
                    }}
                  >
                    ↺ Show All
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ── RIGHT CARD: Overall Record Quality ── */}
          <div style={{
            background: '#fff', borderRadius: 12,
            border: '1px solid #e0e6ed',
            boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
            padding: '20px 24px',
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
          }}>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>Overall Record Quality</div>
              <div style={{ fontSize: 11, color: ipActiveDimension ? ipActiveDimension.color : '#94a3b8', marginTop: 3, fontWeight: ipActiveDimension ? 600 : 400 }}>
                {ipActiveDimension ? `Filtered: ${ipActiveDimension.name} dimension` : ''}
              </div>
            </div>

            {/* Success Bar */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{
                  fontSize: 10, fontWeight: 700, color: '#15803d',
                  background: '#dcfce7', border: '1px solid #bbf7d0',
                  borderRadius: 20, padding: '3px 12px', letterSpacing: '0.05em',
                }}>SUCCESS RECORDS</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#15803d' }}>{successPct}%</span>
              </div>
              <div style={{ width: '100%', height: 26, background: '#f0fdf4', borderRadius: 8, overflow: 'hidden', border: '1px solid #bbf7d0' }}>
                <div style={{
                  width: `${successPct}%`, height: '100%',
                  background: 'linear-gradient(90deg, #16a34a, #4ade80)',
                  borderRadius: 8, transition: 'width 0.6s ease',
                }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5 }}>
                <span style={{ fontSize: 10, color: '#94a3b8' }}>0%</span>
                <span style={{ fontSize: 10, color: '#94a3b8' }}>100%</span>
              </div>
            </div>

            {/* Failed Bar */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{
                  fontSize: 10, fontWeight: 700, color: '#b91c1c',
                  background: '#fef2f2', border: '1px solid #fecaca',
                  borderRadius: 20, padding: '3px 12px', letterSpacing: '0.05em',
                }}>FAILED RECORDS</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#b91c1c' }}>{failedPct}%</span>
              </div>
              <div style={{ width: '100%', height: 26, background: '#fef2f2', borderRadius: 8, overflow: 'hidden', border: '1px solid #fecaca' }}>
                <div style={{
                  width: `${failedPct}%`, height: '100%',
                  background: 'linear-gradient(90deg, #dc2626, #f87171)',
                  borderRadius: 8, transition: 'width 0.6s ease',
                }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5 }}>
                <span style={{ fontSize: 10, color: '#94a3b8' }}>0%</span>
                <span style={{ fontSize: 10, color: '#94a3b8' }}>100%</span>
              </div>
            </div>
          </div>

        </div>
      );
    })()}

    {/* ── Recent Analysis — always shown, uses threadPayload directly ── */}
     <RecentAnalysis
  key={threadPayload?.userId || 0}
  threadPayload={
    threadPayload?.token && threadPayload?.userId
      ? threadPayload
      : null
  }
  preloadedRows={issuePredictorData?.threads || []}
  onRowClick={(row) => {
  setSelectedRow(row);
  setModalTab('overview');
  setModalData(null);
  setModalLoading(true);
  fetchThreadDetail({
    token:                threadPayload?.token,
    organizationConfigId: threadPayload?.organizationConfigId || organizationConfigId,
    userId: threadPayload?.userId,
    threadId:             row.id,
  })
    .then((data) => setModalData(data))
    .catch(() => setModalData(null))
    .finally(() => setModalLoading(false));
}}
/>

 </div>

  {/* ── Session Drill-Down Modal ── */}
  {selectedRow && (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) setSelectedRow(null); }}
      onKeyDown={(e) => { if ((e.key === 'Escape' || e.key === 'Enter') && e.target === e.currentTarget) setSelectedRow(null); }}
      role="button"
      tabIndex={0}
      aria-label="Close modal"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div style={{
        width: 860, maxWidth: 'calc(100vw - 100px)',
        height: '82vh', maxHeight: 720,
        background: '#fff', border: '1.5px solid #b0b8c4',
        borderRadius: 10, boxShadow: '0 24px 64px rgba(0,0,0,.22)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        position: 'relative', zIndex: 10000,
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1.5px solid #b0b8c4',
          background: '#e4e9f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        }}>
          <div>
             <div style={{ fontSize: 13, fontWeight: 700, color: '#2c3a50' }}>
              {selectedRow.table_name || '—'}
            </div>
            <div style={{ fontSize: 10, color: '#7a869a', marginTop: 3 }}>
              {selectedRow.date}
            </div>
          </div>
          <button
            onClick={() => setSelectedRow(null)}
            style={{
              width: 28, height: 28, background: '#d4dae3', border: '1px solid #b0b8c4',
              borderRadius: 4, cursor: 'pointer', fontSize: 14, color: '#7a869a',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#c47c7c'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#d4dae3'; e.currentTarget.style.color = '#7a869a'; }}
          >✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1.5px solid #b0b8c4', background: '#fff', flexShrink: 0 }}>
          {['overview', 'columns'].map(t => (
            <button key={t} onClick={() => setModalTab(t)} style={{
              padding: '8px 18px', fontSize: 11, cursor: 'pointer',
              borderBottom: modalTab === t ? '2px solid #4a6fa5' : '2px solid transparent',
              marginBottom: -1.5, color: modalTab === t ? '#4a6fa5' : '#7a869a',
              fontWeight: modalTab === t ? 700 : 400, textTransform: 'capitalize',
              transition: 'color .15s', background: 'none', border: 'none', fontFamily: 'inherit',
            }}>{t === 'overview' ? 'Overview' : 'Columns'}</button>
          ))}
        </div>

       {/* Body */}
<div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

  {modalLoading && (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
      <Loader className="w-7 h-7 text-blue-600 animate-spin" />
    </div>
  )}

  {!modalLoading && modalTab === 'overview' && (() => {
    const tableData = modalData?.tables?.[0] || null;
const overall = tableData?.overall_analysis || selectedRow.raw?.overall_analysis || {};
            const totalRecords   = overall.total_records            ?? selectedRow.total_records   ?? 0;
            const failedRecords  = overall.total_failed_records     ?? selectedRow.failed_records  ?? 0;
            const rulesApplied   = overall.total_rules_applied      ?? selectedRow.rules_applied   ?? 0;
            const colsWithIssues = overall.columns_with_issues      ?? 0;

           const dimBar = tableData?.analyze_response?.dimension_bar_chart || tableData?.dimension_bar_chart || selectedRow.raw?.dimension_bar_chart || [];
const dimPie = tableData?.analyze_response?.dimension_pie_chart || tableData?.dimension_pie_chart || selectedRow.raw?.dimension_pie_chart || [];
            const COLORS = { Accuracy:'#4a6fa5', Conformity:'#7da87d', Completeness:'#e6a817', Uniqueness:'#c47c7c', Validity:'#8e72c8', Reasonableness:'#e07a5f' };

            return (
              <>
                {/* 4 KPIs */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
                  {[
                    { label: 'Total Records',      value: totalRecords.toLocaleString(),  color: '#2c3a50' },
                    { label: 'Rules Applied',       value: rulesApplied,                   color: '#2c3a50' },
                    { label: 'Columns with Issues', value: colsWithIssues,                 color: '#c47c7c' },
                    { label: 'Failed Records',      value: failedRecords.toLocaleString(), color: '#c47c7c' },
                 ].map(kpi => (
                    <div key={kpi.label} style={{
                      background: '#fff', borderRadius: 12, padding: '16px 18px',
                      border: '1px solid #e0e6ed',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.07)',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 0 6px 1px rgba(30,58,138,0.2)'; e.currentTarget.style.borderColor = '#1e3a8a'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.07)'; e.currentTarget.style.borderColor = '#e0e6ed'; }}
                      role="status"
                      aria-label={`${kpi.label}: ${kpi.value}`}
                    >
                      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: '#666', marginBottom: 8, fontWeight: 600 }}>{kpi.label}</div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: kpi.color, lineHeight: 1 }}>{kpi.value}</div>
                    </div>
                  ))}
                </div>

                {/* Dimension Summary */}
                {dimBar.length > 0 && (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#2c3a50', textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: '1px solid #e4e9f0', paddingBottom: 6 }}>
                      Dimension Summary
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                      <thead>
                        <tr>{['Dimension','Records','Success %'].map(h => (
                          <th key={h} style={{ textAlign: 'left', padding: '6px 8px', background: '#e4e9f0', borderBottom: '1.5px solid #b0b8c4', color: '#7a869a', fontSize: 9, textTransform: 'uppercase', letterSpacing: '.06em' }}>{h}</th>
                        ))}</tr>
                      </thead>
                      <tbody>
                        {dimBar.map(d => {
                          const pie   = dimPie.find(p => p.dimension === d.dimension);
                          const count = pie?.count ?? 0;
                          const hasIssue = (d.failed_percentage ?? d.failed_pct ?? 0) > 0;
                          const succPct  = d.success_percentage ?? d.success_pct ?? 0;
                          return (
                            <tr key={d.dimension} style={{ background: hasIssue ? '#fdf0f0' : 'transparent' }}>
                              <td style={{ padding: '7px 8px', borderBottom: '1px solid #e4e9f0' }}>
                                <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: COLORS[d.dimension] || '#ccc', marginRight: 6 }} />
                                {d.dimension}
                              </td>
                              <td style={{ padding: '7px 8px', borderBottom: '1px solid #e4e9f0' }}>{count.toLocaleString()}</td>
                              <td style={{ padding: '7px 8px', borderBottom: '1px solid #e4e9f0' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <div style={{ flex: 1, height: 5, background: '#d4dae3', borderRadius: 2, overflow: 'hidden' }}>
                                    <div style={{ width: `${succPct}%`, height: '100%', background: hasIssue ? '#c47c7c' : '#7da87d', borderRadius: 2 }} />
                                  </div>
                                  <span style={{ fontSize: 10, fontWeight: hasIssue ? 700 : 400, color: hasIssue ? '#c47c7c' : '#2c3a50' }}>
                                    {succPct.toFixed(1)}%
                                  </span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </>
                )}
                {dimBar.length === 0 && (
                  <div style={{ background: '#e4e9f0', padding: 12, borderRadius: 6, fontSize: 10, color: '#7a869a', textAlign: 'center' }}>
                    No dimension data available for this session.
                  </div>
                )}
              </>
            );
          })()}

         {!modalLoading && modalTab === 'columns' && (() => {
  const tableData = modalData?.tables?.[0] || null;
const colRulesRaw = tableData?.analyze_response?.column_rules_summary || tableData?.column_rules_summary || selectedRow.raw?.column_rules_summary || [];
const colRules = [...colRulesRaw].sort((a, b) => (b.failed_rules ?? 0) - (a.failed_rules ?? 0));
            if (colRules.length === 0) return (
              <div style={{ background: '#e4e9f0', padding: 20, borderRadius: 6, fontSize: 11, color: '#7a869a', textAlign: 'center' }}>
                No column analysis data available for this session.
              </div>
            );
            return (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#2c3a50', textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: '1px solid #e4e9f0', paddingBottom: 6 }}>
                  Column Analysis ({colRules.length} columns)
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                  <thead>
                    <tr>{['Table','Column','Mandatory','Records','Rules','Action'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '6px 8px', background: '#e4e9f0', borderBottom: '1.5px solid #b0b8c4', color: '#7a869a', fontSize: 9, textTransform: 'uppercase', letterSpacing: '.06em' }}>{h}</th>
                    ))}</tr>
                  </thead>
                 <tbody>
  {colRules.map((c, i) => renderColumnRow(c, i, expandedColRows, setExpandedColRows, modalData))}
</tbody>
                </table>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  )}
  </>
  );
};

IssuePredictorDashboard.propTypes = {
  issuePredictorData: PropTypes.object,
  issueLoading:       PropTypes.bool,
  threadPayload: PropTypes.shape({
    token:                PropTypes.string.isRequired,
    organizationConfigId: PropTypes.string.isRequired,
    userId:               PropTypes.string.isRequired,
  }),
};
IssuePredictorDashboard.defaultProps = {
  issuePredictorData: null,
  issueLoading:       false,
  threadPayload:      null,
};

export default IssuePredictorDashboard;